import {
  HHChip,
  HHCollapsibleTable,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useState } from 'react';
import { PackageType } from '../../types';
import { calculateChipColor } from '../../utils/contract-utils';
import { computeEffectiveRange } from '../../utils/date-helper';
import { IDiscount } from '../../utils/discount-utils';
import LoadingComponent from '../utils/loading';
import VbdDetail from './vbd-detail';

export interface VbdListProps {
  vbdsData?: IDiscount[];
  loading?: boolean;
  availablePackages: PackageType[];
  partnershipMap: Map<number, string>;
  clientMap: Map<number, string>;
  onEdit?: (discountDefinitionId: number) => void;
}

// ACCOUNTS-AND-PLAN-MIGRATION: clientId/partnershipId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
function getVbdLevel(
  clientId?: number | null,
  partnershipId?: number | null,
): string {
  if (clientId != null && partnershipId != null) {
    return 'Mixed VBD';
  }

  if (clientId != null) {
    return 'Client Level';
  }

  return 'Partnership Level';
}

const VbdList = ({
  vbdsData,
  loading = true,
  availablePackages,
  partnershipMap,
  clientMap,
  onEdit,
}: VbdListProps): JSX.Element => {
  const [openRowId, setOpenRowId] = useState<number | null>(null);

  const handleOpenRow =
    (id: number): React.MouseEventHandler<HTMLButtonElement> =>
    (): void => {
      setOpenRowId(openRowId !== id ? id : null);
    };

  const headers = [
    { align: 'left' as const, headerTitle: 'Details' },
    { align: 'left' as const, headerTitle: 'Partnership' },
    { align: 'left' as const, headerTitle: 'Client' },
    { align: 'left' as const, headerTitle: 'Date Range' },
    { align: 'left' as const, headerTitle: 'Status' },
  ];

  return (
    <HHCollapsibleTable
      headers={headers}
      emptyRowContent={
        loading ? (
          <LoadingComponent center />
        ) : (
          <HHStack direction="row" padding={4} justifyContent="center">
            <HHTypography hhVariant="muted">
              No volume based discounts to display
            </HHTypography>
          </HHStack>
        )
      }
      rows={
        vbdsData
          ? vbdsData.map(vbd => ({
              open: openRowId === vbd.discountDefinitionId,
              setOpen: handleOpenRow(vbd.discountDefinitionId),
              rowId: vbd.discountDefinitionId,
              collapsibleRowContent: (
                <VbdDetail
                  discount={vbd}
                  availablePackages={availablePackages}
                  onEdit={onEdit}
                />
              ),
              // ACCOUNTS-AND-PLAN-MIGRATION: vbd.clientId/vbd.partnershipId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
              columnValues: [
                {
                  columnItem: getVbdLevel(vbd.clientId, vbd.partnershipId),
                },
                {
                  columnItem:
                    (vbd.partnershipId &&
                      partnershipMap.get(vbd.partnershipId)) ||
                    '--',
                },
                {
                  columnItem:
                    (vbd.clientId && clientMap.get(vbd.clientId)) || '--',
                },
                {
                  columnItem: computeEffectiveRange(vbd.startDate, vbd.endDate),
                },
                {
                  columnItem: (
                    <HHChip
                      hhVariant="variant-bypass"
                      color={calculateChipColor(vbd.status)}
                      size="small"
                      label={vbd.status}
                    />
                  ),
                },
              ],
            }))
          : []
      }
    />
  );
};

export default VbdList;
